import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {take, takeUntil} from 'rxjs/operators';
import {ChatMessage} from "../../models/chat-message";
import {ChatMessageChunk} from "../../models/chat-message-chunk";
import {TelemetryService} from '../telemetry/telemetry.service';
import {LoggingService} from '../logging/logging.service';
import {ConversationManager} from '../conversation/conversation-manager.service';
import {WebSocketService} from '../websocket.service';
import {MessageType, DEFAULT_MESSAGE_TYPE} from '../../models/message-type';
import {MessageRegistry} from './message-registry';
import {trace, context} from '@opentelemetry/api';
import {MessageStoreService} from '../persistence/message-store.service';


/**
 * Service responsible for processing and assembling chat message chunks
 * Handles streaming updates from the WebSocket and assembles them into
 * complete messages for display.
 */
@Injectable({
  providedIn: 'root',
})
export class ChatMessageProcessor implements OnDestroy {
  private messageRegistry: MessageRegistry;
  private currentResponseMessage: ChatMessage | null = null;
  private currentMessageId: string | null = null;

  // ✅ FIX: MessageStore is the single source of truth for messages
  // Processor only handles WebSocket chunk assembly, doesn't mirror MessageStore
  private messages$: Observable<ChatMessage[]>;
  private destroy$ = new Subject<void>();

  constructor(
    private telemetryService: TelemetryService,
    private logger: LoggingService,
    private conversationManager: ConversationManager,
    private webSocketService: WebSocketService,
    private messageStore: MessageStoreService // ✅ Inject MessageStore
  ) {
    this.messageRegistry = new MessageRegistry(this.logger);

    // ✅ Subscribe to MessageStore as single source of truth for reads
    this.messages$ = this.messageStore.messages$;

    // Processor handles WebSocket chunk assembly only
    // MessageStore is the single source of truth for message storage and retrieval
    // Components/Services subscribe to MessageStore directly for message updates
    this.logger.info('ChatMessageProcessor', 'Processor initialized - will process WebSocket chunks only');
  }

  /**
   * Cleanup resources when service is destroyed
   */
  ngOnDestroy(): void {
    this.logger.info('ChatMessageProcessor', 'Destroying service');
    this.destroy$.next();
    this.destroy$.complete();

    // PHASE 2: Clean up MessageRegistry
    this.messageRegistry.destroy();

    // Clean up any pending message data
    this.clearMessages();

    // ✅ FIX: Get current messages from MessageStore to cleanup BehaviorSubjects
    this.messages$.pipe(take(1)).subscribe(messages => {
      messages.forEach(msg => {
        if (msg.content) {
          try {
            msg.content.complete();
          } catch (e) {
            // Ignore errors from already completed subjects
          }
        }
      });
    });
  }

  // ✅ REMOVED: updateMessages() method
  // Processor now subscribes directly to MessageStore.messages$ (single source of truth)
  // No manual synchronization needed - reactive updates happen automatically

  /**
   * Clear message processing state
   * ✅ FIX: No longer maintains local messages array - MessageStore is source of truth
   */
  clearMessages(): void {
    this.currentResponseMessage = null;
    this.currentMessageId = null;
    this.messageRegistry.cleanup();
    // ✅ REMOVED: this.messages = [] - MessageStore handles message state
  }

  /**
   * Handle incoming message chunks from WebSocket
   */
  handleIncomingMessageChunk(chunk: ChatMessageChunk): void {
    if (!chunk) return;

    const receiveTime = Date.now();

    // Create tracing span for message chunk processing
    const tracer = this.telemetryService.getTracer();
    if (!tracer) {
      console.warn('Tracer not available, manual span will not be created');
      // Continue processing without span
    }
    const processSpan = tracer ? tracer.startSpan('chat_chunk_receive') : null;

    // Extract trace context from the chunk if available
    let parentContext = context.active();
    if (chunk.tracingContext && chunk.tracingContext.traceId && chunk.tracingContext.spanId) {
      try {
        const parentSpanContext = {
          traceId: chunk.tracingContext.traceId,
          spanId: chunk.tracingContext.spanId,
          traceFlags: chunk.tracingContext.traceFlags || 1,
          isRemote: true
        };
        parentContext = trace.setSpanContext(context.active(), parentSpanContext as any);
      } catch (e) {
        this.logger.warn('Failed to set parent span context from chunk', String(e));
      }
    }

    if (processSpan) {
      processSpan.setAttributes({
        'message.id': chunk.messageId || 'unknown',
        'message.conversation_id': chunk.conversationId || 'unknown',
        'message.sequence_number': chunk.sequenceNumber || 0,
        'message.is_complete': chunk.complete || false,
        'message.content_length': chunk.content?.length || 0,
        'message.type': chunk.messageType || 'unknown',
        'frontend.component': 'message-processor',
        'websocket.operation': 'receive_chunk'
      });
    }

    if (chunk.tracingContext && processSpan) {
      processSpan.setAttribute('trace.parent_trace_id', chunk.tracingContext.traceId || 'unknown');
      processSpan.setAttribute('trace.parent_span_id', chunk.tracingContext.spanId || 'unknown');
    }

    try {
      const currentClientId = this.webSocketService.getUserIdentifier();

      // 🎯 CONVERSATION FILTERING: Enhanced filtering based on userEmail and conversation context
      const messageType = chunk.messageType || DEFAULT_MESSAGE_TYPE;

      if (chunk.conversationId) {
        // This is a conversation message - apply strict filtering
        if (messageType === MessageType.CONVERSATION && !this.conversationManager.belongsToThisTab(chunk.conversationId, currentClientId)) {
          this.logger.info('[FRONTEND-MSG-PROCESSOR] Filtering out conversation chunk from different tab', {
            chunkConversationId: chunk.conversationId,
            messageType,
            messageId: chunk.messageId,
            sequenceNumber: chunk.sequenceNumber
          });
          return; // Skip processing - this chunk belongs to a different tab's conversation
        }
      } else {
        // No conversation ID - these are likely initial/system messages
        // Since backend routes messages to correct client via subscription topic (/topic/chat/{userEmail}),
        // we trust that any message received on our topic is intended for this tab.
        // The previous logic using conversation count was flawed and prevented Tab 2+ from working.

        if (messageType === MessageType.SYSTEM) {
          this.logger.debug('[FRONTEND-MSG-PROCESSOR] Processing system message', `${chunk.messageId}:${chunk.sequenceNumber}`);
        } else {
          this.logger.info('[FRONTEND-MSG-PROCESSOR] Processing initial message for this client', {
            messageId: chunk.messageId,
            sequenceNumber: chunk.sequenceNumber,
            currentClientId: currentClientId,
            reason: 'Backend routed to our specific topic'
          });
        }
      }

      // Compact chunk receive log
      const msgId = chunk.messageId?.slice(-8) || 'unknown';
      this.logger.info('[MSG-PROC]', `chunk#${chunk.sequenceNumber} | ${msgId} | len:${chunk.content?.length || 0} | complete:${chunk.complete}`);
      
      // Track message received
      this.telemetryService.trackEvent(
        'chat_message_chunk_received',
        {
          isBot: chunk.bot,
          isComplete: chunk.complete,
          contentLength: chunk.content ? chunk.content.length : 0,
          sequenceNumber: chunk.sequenceNumber,
          messageId: chunk.messageId,
          timeReceived: receiveTime,
          traceId: chunk.tracingContext?.traceId,
          spanId: chunk.tracingContext?.spanId
        },
        chunk.tracingContext?.traceId,
        chunk.tracingContext?.spanId
      );

      // PHASE 2: Message-centric approach using MessageRegistry
      // Always use MessageRegistry as single source of truth
      const messageInfo = this.messageRegistry.getOrCreateMessage(chunk.messageId, () => {
        // Factory function to create new bot message when needed
        const messageType = chunk.messageType || DEFAULT_MESSAGE_TYPE;

        // 🔥 FIX: Don't try to reuse empty placeholders - backend generates its own messageIds
        // The "reuse" logic was causing phantom "Thinking..." messages because it would find
        // the WRONG empty message (e.g., bot greeting still loading) instead of the intended placeholder.
        // Since frontend-generated placeholder IDs never match backend chunk IDs anyway,
        // we should just create a new message for each backend response.

        // Create completely new message
        const newMessage = this.createBotMessage(chunk.conversationId, messageType);
        newMessage.messageId = chunk.messageId; // Use backend's messageId
        newMessage.generationStartTime = Date.now();
        newMessage.tracingContext = chunk.tracingContext;
        this.addMessageToArray(newMessage);
        return newMessage;
      });

      // Update current state to match the registry
      this.currentResponseMessage = messageInfo.botMessage;
      this.currentMessageId = chunk.messageId;

      // Update conversation context if missing
      if (!this.currentResponseMessage.conversationId && chunk.conversationId) {
        this.currentResponseMessage.conversationId = chunk.conversationId;
      }
      if (!this.currentResponseMessage.messageType && chunk.messageType) {
        this.currentResponseMessage.messageType = chunk.messageType;
      }

      // Add chunk to MessageRegistry tracker (with built-in deduplication)
      this.messageRegistry.addChunk(chunk.messageId, chunk);
      
      // Mark complete if this is the final chunk
      if (chunk.complete) {
        const msgId = chunk.messageId?.slice(-8) || 'unknown';
        this.logger.info('[MSG-PROC]', `completion signal | ${msgId} | seq#${chunk.sequenceNumber} | total:${messageInfo.tracker.chunks.length}`);

        this.messageRegistry.markCompleted(chunk.messageId);

        // If this is a completion chunk and we have the current response message,
        // set the end time and calculate response time immediately
        if (this.currentResponseMessage && this.currentMessageId === chunk.messageId) {
          // Mark the completion time when the final chunk is received
          this.currentResponseMessage.generationEndTime = Date.now();
          // Calculate response time from the most recent user message
          this.calculateResponseTimeFromUserMessage(this.currentResponseMessage);
          // ✅ FIX: MessageStore already emits updates, no need to re-emit
        }
      }
      // Process the chunks we have so far (ordered by sequence number)
      const processStartTime = Date.now();

      this.processMessageChunks(chunk.messageId);

      if (processSpan) {
        processSpan.setStatus({code: 1}); // OK
        processSpan.addEvent('chunk_processed_successfully');
      }

    } catch (error) {
      if (processSpan) {
        processSpan.setStatus({code: 2, message: error instanceof Error ? error.message : 'Unknown error'}); // ERROR
        processSpan.recordException(error as Error);
      }

      this.logger.error('ChatMessageProcessor', 'Error handling incoming message chunk', error);
      this.telemetryService.trackError('chat_message_handling_error', {
        error: String(error),
        traceId: chunk.tracingContext?.traceId
      });
    } finally {
      if (processSpan) {
        processSpan.end();
      }
    }
  }

  /**
   * Process accumulated message chunks
   */
  private processMessageChunks(messageId: string): void {
    const messageInfo = this.messageRegistry.getMessage(messageId);
    if (!messageInfo) return;

    const processTime = Date.now();
    // Chunks are already sorted by MessageRegistry.addChunk() using binary search insertion
    // No need to sort here - this eliminates O(n² log n) complexity
    const chunks = messageInfo.tracker.chunks;

    // Compact processing log
    const msgId = messageId?.slice(-8) || 'unknown';
    this.logger.info('[MSG-PROC]', `processing | ${msgId} | ${chunks.length} chunks | complete:${messageInfo.tracker.completeReceived}`);
    // Filter out empty completion chunks for content aggregation
    const contentChunks = chunks.filter(chunk =>
      chunk.content && chunk.content.length > 0);
    // Combine content into a single string
    const combinedContent = contentChunks
      .map(chunk => chunk.content)
      .join('');

    if (this.currentResponseMessage && this.currentMessageId === messageId) {
      // Get previous content to detect actual changes
      const previousContent = this.currentResponseMessage.content.getValue();
      const contentChanged = combinedContent !== previousContent;

      // Compact content update log (only if changed)
      if (contentChanged) {
        const msgId = messageId?.slice(-8) || 'unknown';
        this.logger.info('[MSG-PROC]', `content update | ${msgId} | ${previousContent.length}→${combinedContent.length} chars`);
      }

      // Update the message content
      this.currentResponseMessage.content.next(combinedContent);

      // Only trigger change detection if content actually changed
      // This prevents O(n²) complexity from array copying on every chunk
      if (contentChanged) {
        // ✅ FIX: MessageStore already emits updates when content changes via BehaviorSubject
        // No need to manually re-emit - reactive pipeline handles it automatically
      }
    } else {
      // Log failure case (messageId mismatch)
      const reason = !this.currentResponseMessage ? 'No currentResponseMessage' :
                     (this.currentMessageId !== messageId ? 'MessageId mismatch' : 'Unknown');

      this.logger.warn('[MSG-PROC]', `Skip update | ${messageId?.slice(-8)} | ${reason}`);
    }
    // If this is the complete message, clean up
    if (messageInfo.tracker.completeReceived) {
      const msgId = messageId?.slice(-8) || 'unknown';
      const missing = this.findMissingSequences(chunks);
      const missingInfo = missing.length > 0 ? ` | MISSING:${missing.join(',')}` : '';
      this.logger.info('[MSG-PROC]', `complete | ${msgId} | ${chunks.length} chunks | ${combinedContent.length} chars${missingInfo}`);
      // Clear the current message references
      if (this.currentMessageId === messageId) {
        this.currentResponseMessage = null;
        this.currentMessageId = null;
      }
      // PHASE 1 HOT FIX: Don't immediately delete the tracker
      // MessageRegistry handles cleanup automatically
    }
  }
  
  /**
   * Find any missing sequence numbers in the chunks
   */
  private findMissingSequences(chunks: ChatMessageChunk[]): number[] {
    if (chunks.length === 0) return [];
    const sequences = chunks.map(c => c.sequenceNumber).sort((a, b) => a - b);
    const missing: number[] = [];
    for (let i = sequences[0]; i < sequences[sequences.length - 1]; i++) {
      if (!sequences.includes(i)) {
        missing.push(i);
      }
    }
    return missing;
  }

  /**
   * Create a bot message with empty content
   */
  private createBotMessage(conversationId?: string, messageType?: MessageType): ChatMessage {
    return {
      bot: true,
      content: new BehaviorSubject<string>(''),
      pictureUrl: 'assets/svg/bot_icon.svg',
      timestamp: this.createTimestamp(),
      messageId: `${Date.now()}-${Math.random().toString(36).substring(2, 10)}`,
      conversationId,
      messageType: messageType || DEFAULT_MESSAGE_TYPE
    };
  }

  /**
   * Create a timestamp for a message
   */
  private createTimestamp(): string {
    const now = new Date();
    const isoString = now.toISOString();
    const nanoseconds = now.getMilliseconds().toString().padEnd(6, '0');
    return isoString.replace('Z', nanoseconds + 'Z');
  }

  /**
   * 🔥 FIX: Parse timestamp robustly to handle mixed formats.
   * Backend returns numeric timestamps, frontend creates ISO strings.
   */
  private parseTimestamp(timestamp: any): number {
    if (!timestamp) return 0;
    if (typeof timestamp === 'number') return timestamp;
    if (typeof timestamp === 'string') {
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) return date.getTime();
      const numericValue = parseInt(timestamp, 10);
      if (!isNaN(numericValue)) return numericValue;
    }
    return 0;
  }

  /**
   * Add message to MessageStore if it doesn't exist already
   * ✅ FIX: Uses MessageStore as single source of truth (no local array)
   */
  private addMessageToArray(message: ChatMessage): void {
    // ✅ FIX: Check MessageStore for duplicates, then upsert
    // MessageStore handles deduplication via messageId
    this.messages$.pipe(take(1)).subscribe(messages => {
      // Check if message already exists by timestamp (legacy check)
      const exists = messages.some(m => m.timestamp === message.timestamp);
      if (!exists) {
        // Add to MessageStore (single source of truth)
        this.messageStore.upsertMessage(message);
        // MessageStore will emit update, which triggers processedMessages$ via ChatService subscription
        this.logger.info('[FRONTEND-MSG-PROCESSOR] Added new message to MessageStore', {
          messageId: message.messageId,
          timestamp: message.timestamp
        });
      }
    });
  }

  /**
   * Calculate response time based on the most recent user message's timestamp
   * ✅ FIX: Gets messages from MessageStore (single source of truth)
   */
  private calculateResponseTimeFromUserMessage(botMessage: ChatMessage): void {
    // ✅ FIX: Get current messages from MessageStore
    this.messages$.pipe(take(1)).subscribe(messages => {
      // Find the most recent user message (messages are sorted with oldest first)
      const userMessages = messages.filter(msg => !msg.bot);
      if (userMessages.length > 0) {
        // Get the timestamp of the most recent user message (last in array since oldest first)
        // 🔥 FIX: Use robust timestamp parsing for mixed format handling
        const lastUserMessage = userMessages[userMessages.length - 1];
        const userMessageTimestamp = lastUserMessage.timestamp ?
          this.parseTimestamp(lastUserMessage.timestamp) :
          Date.now();
        const endTime = botMessage.generationEndTime || Date.now();
        // Calculate response time from user message to end of bot response
        const responseTime = endTime - userMessageTimestamp;
        if (responseTime > 0) {
          botMessage.responseTimeMs = responseTime;
          this.logger.info('ChatMessageProcessor', `Total response time from user message: ${responseTime}ms`);
        }
      } else if (botMessage.generationStartTime && botMessage.generationEndTime) {
        // Fallback to the old calculation if no user messages found
        const responseTime = botMessage.generationEndTime - botMessage.generationStartTime;
        if (responseTime > 0) {
          botMessage.responseTimeMs = responseTime;
          this.logger.info('ChatMessageProcessor', `Generation time (fallback): ${responseTime}ms`);
        }
      }
    });
  }

  /**
   * Update the content of a bot message directly (used for HTTP responses)
   * ✅ FIX: Uses MessageStore as single source of truth
   */
  updateBotMessageContent(content: string): void {
    if (this.currentResponseMessage) {
      // Set generation end time right before updating the message
      this.currentResponseMessage.generationEndTime = Date.now();
      // Calculate response time from the most recent user message
      this.calculateResponseTimeFromUserMessage(this.currentResponseMessage);
      this.currentResponseMessage.content.next(content);
      this.currentResponseMessage = null;

      // ✅ FIX: MessageStore already emits updates, no need to re-emit

      this.logger.info('ChatMessageProcessor', 'Updated bot message with HTTP response', {
        responseLength: content.length
      });
    } else {
      // Create a new bot message if needed
      const newBotMessage = this.createBotMessage();
      // For new messages created directly, set both start and end time to be the same
      const now = Date.now();
      newBotMessage.generationStartTime = now;
      newBotMessage.generationEndTime = now;
      newBotMessage.content.next(content);

      // Add to MessageStore
      this.addMessageToArray(newBotMessage);

      this.logger.info('ChatMessageProcessor', 'Created new bot message with HTTP response', {
        responseLength: content.length
      });
    }
  }

  /**
   * Prepare a temporary bot message for loading state
   * ✅ FIX: Checks MessageStore for pending bot messages (single source of truth)
   */
  prepareTemporaryBotMessage(conversationId?: string, messageType?: MessageType): ChatMessage | null {
    // ✅ FIX: Check MessageStore for pending bot messages
    let hasPendingBot = false;
    this.messages$.pipe(take(1)).subscribe(messages => {
      hasPendingBot = messages.some(
        m => m.bot && (!m.content.getValue() || m.content.getValue().trim() === '')
      );
    });

    if (!this.currentResponseMessage && !hasPendingBot) {
      const botMessage = this.createBotMessage(conversationId, messageType);
      // Track generation start time
      botMessage.generationStartTime = Date.now();
      this.currentResponseMessage = botMessage;
      this.addMessageToArray(botMessage);
      return botMessage;
    }

    return this.currentResponseMessage;
  }

  /**
   * Extract conversation ID from a message chunk.
   * For now, we'll derive it from the messageId pattern.
   * In future iterations, the backend could include this directly.
   */
  private extractConversationId(chunk: ChatMessageChunk): string | undefined {
    // The current messageId format is: ${Date.now()}-${randomString}
    // For conversation ID, we'll use the first part which represents when the conversation started
    if (chunk.messageId) {
      // For bot responses, the conversation ID is derived from the user message that triggered it
      // Since all chunks in a conversation share the same messageId, we can use that as conversation ID
      return chunk.messageId;
    }
    return undefined;
  }

  /**
   * Start a new conversation context when a user sends a message
   * Reuses existing conversation ID if available to maintain consistency
   */
  startNewConversation(userMessageId: string): string {
    const currentClientId = this.webSocketService.getUserIdentifier();

    // First, try to find an existing active conversation for this client
    const existingConversations = this.conversationManager.getConversationsForClient(currentClientId);
    const activeConversation = existingConversations.find(conv => conv.isActive);

    let conversationId: string;

    if (activeConversation) {
      // Reuse existing conversation ID to maintain consistency with backend
      conversationId = activeConversation.conversationId;
      this.logger.info('[FRONTEND-MSG-PROCESSOR] Reusing existing conversation', {
        conversationId,
        userMessageId,
        originatingClientId: currentClientId
      });
    } else {
      // Only create new conversation if none exists
      conversationId = userMessageId; // Use user message ID as conversation ID
      this.conversationManager.createConversation(conversationId, currentClientId);
      this.logger.info('[FRONTEND-MSG-PROCESSOR] Started new DRAFT conversation', {
        conversationId,
        originatingClientId: currentClientId
      });
    }

    return conversationId;
  }

  /**
   * PHASE 2: Get MessageRegistry statistics for debugging/monitoring
   */
  getRegistryStats() {
    return this.messageRegistry.getStats();
  }

  /**
   * PHASE 2: Get MessageRegistry metrics for late chunk monitoring
   */
  getMetrics() {
    return this.messageRegistry.getMetrics();
  }

  /**
   * Get message tracker for debugging chunk-level issues
   * Returns chunk data for a specific message ID
   */
  getMessageTracker(messageId: string): any | null {
    return this.messageRegistry.getMessage(messageId)?.tracker || null;
  }
}
